import { collection, collectionGroup, doc, getDoc, getDocs, query, where } from "firebase/firestore";

import { db } from "../Firebase";
import {
    kCompositionsCollectionName,
    kCompositionLayerClipsSubcollectionName,
    kCompositionLayersSubcollectionName,
    kSequencesCollectionName,
    kSequenceLayerClipsSubcollectionName,
    kSequenceLayersSubcollectionName,
} from "../utils/CollectionConstants";

type ProjectRecordLike = {
    id?: string | null;
    title?: string | null;
    name?: string | null;
};

type OwnerConfig = {
    id: string;
    kind: AssetUsageOwnerKind;
    name: string;
};

type StorageShape = {
    collectionName: string;
    layersSubcollectionName: string;
    clipsSubcollectionName: string;
};

type OwnerMaps = {
    compositionById: Map<string, OwnerConfig>;
    sequenceById: Map<string, OwnerConfig>;
};

export type AssetUsageOwnerKind = "composition" | "sequence";

export type AssetUsageOwner = {
    id: string;
    kind: AssetUsageOwnerKind;
    name: string;
};

export type FileUsageResult = {
    fileId: string;
    owners: AssetUsageOwner[];
};

const normalizeId = (value: any): string => String(value ?? "").trim();

const uniqueStrings = (values: any[]): string[] => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const value of values) {
        const id = normalizeId(value);
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push(id);
    }
    return out;
};

const ownerName = (record: ProjectRecordLike | undefined): string => {
    const title = String(record?.title ?? record?.name ?? "").trim();
    return title || "Untitled";
};

const dedupeOwners = (owners: AssetUsageOwner[]): AssetUsageOwner[] => {
    const out: AssetUsageOwner[] = [];
    const seen = new Set<string>();
    for (const owner of owners) {
        const key = `${owner.kind}:${owner.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(owner);
    }
    return out;
};

const addOwnerUsage = (
    usageByFileId: Map<string, AssetUsageOwner[]>,
    fileId: string,
    owner: AssetUsageOwner,
): void => {
    const current = usageByFileId.get(fileId) ?? [];
    current.push(owner);
    usageByFileId.set(fileId, dedupeOwners(current));
};

export const buildReferencedAssetIds = (args: {
    assetId?: any;
    sources?: any[] | null;
    referencedAssetIds?: any[] | null;
}): string[] => {
    if (Array.isArray(args.referencedAssetIds) && args.referencedAssetIds.length > 0) {
        return uniqueStrings(args.referencedAssetIds);
    }

    const sourceAssetIds = Array.isArray(args.sources)
        ? args.sources.map((source) => source?.assetId)
        : [];

    return uniqueStrings([
        ...sourceAssetIds,
        args.assetId,
    ]);
};

const getOrderedLayerIds = async (args: {
    ownerRef: any;
    layersSubcollectionName: string;
}): Promise<string[]> => {
    const ownerSnap = await getDoc(args.ownerRef);
    if (!ownerSnap.exists()) return [];

    const ownerData: any = ownerSnap.data() || {};
    if (Array.isArray(ownerData?.orderedLayerIds) && ownerData.orderedLayerIds.length > 0) {
        return uniqueStrings(ownerData.orderedLayerIds);
    }

    const layersSnap = await getDocs(collection(args.ownerRef, args.layersSubcollectionName));
    return uniqueStrings(layersSnap.docs.map((layerDoc) => layerDoc.id));
};

const getClipDocsForLayer = async (args: {
    ownerRef: any;
    layerId: string;
    layersSubcollectionName: string;
    clipsSubcollectionName: string;
}) => {
    const layerRef = doc(args.ownerRef, args.layersSubcollectionName, args.layerId);
    const layerSnap = await getDoc(layerRef);
    if (!layerSnap.exists()) return [];

    const layerData: any = layerSnap.data() || {};
    const orderedClipIds = Array.isArray(layerData?.orderedClipIds)
        ? uniqueStrings(layerData.orderedClipIds)
        : [];

    if (orderedClipIds.length > 0) {
        const docs = [];
        for (const clipId of orderedClipIds) {
            const clipSnap = await getDoc(doc(layerRef, args.clipsSubcollectionName, clipId));
            if (clipSnap.exists()) docs.push(clipSnap);
        }
        return docs;
    }

    const clipsSnap = await getDocs(collection(layerRef, args.clipsSubcollectionName));
    return clipsSnap.docs;
};

const scanOwner = async (args: {
    owner: OwnerConfig;
    storage: StorageShape;
    fileIds: string[];
    usageByFileId: Map<string, AssetUsageOwner[]>;
}): Promise<void> => {
    const fileIdsSet = new Set(args.fileIds);
    const ownerRef = doc(db, args.storage.collectionName, args.owner.id);
    const orderedLayerIds = await getOrderedLayerIds({
        ownerRef,
        layersSubcollectionName: args.storage.layersSubcollectionName,
    });
    if (orderedLayerIds.length === 0) return;

    for (const layerId of orderedLayerIds) {
        const clipDocs = await getClipDocsForLayer({
            ownerRef,
            layerId,
            layersSubcollectionName: args.storage.layersSubcollectionName,
            clipsSubcollectionName: args.storage.clipsSubcollectionName,
        });

        for (const clipDoc of clipDocs) {
            const data: any = clipDoc.data() || {};
            const refs = buildReferencedAssetIds({
                referencedAssetIds: Array.isArray(data?.referencedAssetIds) ? data.referencedAssetIds : undefined,
                assetId: data?.assetId,
                sources: Array.isArray(data?.sources) ? data.sources : undefined,
            });

            for (const ref of refs) {
                if (!fileIdsSet.has(ref)) continue;
                addOwnerUsage(args.usageByFileId, ref, {
                    id: args.owner.id,
                    kind: args.owner.kind,
                    name: args.owner.name,
                });
            }
        }
    }
};

const createOwners = (args: {
    compositions?: ProjectRecordLike[];
    sequences?: ProjectRecordLike[];
}): OwnerConfig[] => {
    const owners: OwnerConfig[] = [];

    for (const composition of args.compositions ?? []) {
        const id = normalizeId(composition?.id);
        if (!id) continue;
        owners.push({
            id,
            kind: "composition",
            name: ownerName(composition),
        });
    }

    for (const sequence of args.sequences ?? []) {
        const id = normalizeId(sequence?.id);
        if (!id) continue;
        owners.push({
            id,
            kind: "sequence",
            name: ownerName(sequence),
        });
    }

    return owners;
};

const getStorageShape = (kind: AssetUsageOwnerKind): StorageShape => {
    if (kind === "composition") {
        return {
            collectionName: kCompositionsCollectionName,
            layersSubcollectionName: kCompositionLayersSubcollectionName,
            clipsSubcollectionName: kCompositionLayerClipsSubcollectionName,
        };
    }

    return {
        collectionName: kSequencesCollectionName,
        layersSubcollectionName: kSequenceLayersSubcollectionName,
        clipsSubcollectionName: kSequenceLayerClipsSubcollectionName,
    };
};

const createOwnerMaps = (args: {
    compositions?: ProjectRecordLike[];
    sequences?: ProjectRecordLike[];
}): OwnerMaps => {
    const compositionById = new Map<string, OwnerConfig>();
    const sequenceById = new Map<string, OwnerConfig>();

    for (const composition of args.compositions ?? []) {
        const id = normalizeId(composition?.id);
        if (!id) continue;
        compositionById.set(id, {
            id,
            kind: "composition",
            name: ownerName(composition),
        });
    }

    for (const sequence of args.sequences ?? []) {
        const id = normalizeId(sequence?.id);
        if (!id) continue;
        sequenceById.set(id, {
            id,
            kind: "sequence",
            name: ownerName(sequence),
        });
    }

    return {
        compositionById,
        sequenceById,
    };
};

const chunkStrings = (values: string[], chunkSize: number): string[][] => {
    const out: string[][] = [];
    for (let i = 0; i < values.length; i += chunkSize) {
        out.push(values.slice(i, i + chunkSize));
    }
    return out;
};

const resolveOwnerFromClipData = (data: any, ownerMaps: OwnerMaps): OwnerConfig | null => {
    const compositionId = normalizeId(data?.compositionId);
    if (compositionId) {
        return ownerMaps.compositionById.get(compositionId) ?? null;
    }

    const sequenceId = normalizeId(data?.sequenceId);
    if (sequenceId) {
        return ownerMaps.sequenceById.get(sequenceId) ?? null;
    }

    return null;
};

const scanProjectOwnersForUsageViaClipQuery = async (args: {
    fileIds: string[];
    compositions?: ProjectRecordLike[];
    sequences?: ProjectRecordLike[];
}): Promise<Map<string, AssetUsageOwner[]>> => {
    const fileIds = uniqueStrings(args.fileIds);
    const usageByFileId = new Map<string, AssetUsageOwner[]>();
    if (fileIds.length === 0) return usageByFileId;

    if (kCompositionLayerClipsSubcollectionName !== kSequenceLayerClipsSubcollectionName) {
        throw new Error("Composition and sequence clip collections differ; collectionGroup lookup is unavailable.");
    }

    const ownerMaps = createOwnerMaps(args);
    if (ownerMaps.compositionById.size === 0 && ownerMaps.sequenceById.size === 0) {
        return usageByFileId;
    }

    const targetFileIds = new Set(fileIds);
    const clipCollectionName = kCompositionLayerClipsSubcollectionName;

    for (const chunk of chunkStrings(fileIds, 10)) {
        const clipsRef = collectionGroup(db, clipCollectionName);
        const clipsQuery = chunk.length === 1
            ? query(clipsRef, where("referencedAssetIds", "array-contains", chunk[0]))
            : query(clipsRef, where("referencedAssetIds", "array-contains-any", chunk));
        const clipsSnap = await getDocs(clipsQuery);

        for (const clipDoc of clipsSnap.docs) {
            const data: any = clipDoc.data() || {};
            const owner = resolveOwnerFromClipData(data, ownerMaps);
            if (!owner) continue;

            const refs = buildReferencedAssetIds({
                referencedAssetIds: Array.isArray(data?.referencedAssetIds) ? data.referencedAssetIds : undefined,
                assetId: data?.assetId,
                sources: Array.isArray(data?.sources) ? data.sources : undefined,
            });

            for (const ref of refs) {
                if (!targetFileIds.has(ref)) continue;
                addOwnerUsage(usageByFileId, ref, {
                    id: owner.id,
                    kind: owner.kind,
                    name: owner.name,
                });
            }
        }
    }

    return usageByFileId;
};

const scanProjectOwnersForUsageByOwnerTraversal = async (args: {
    fileIds: string[];
    compositions?: ProjectRecordLike[];
    sequences?: ProjectRecordLike[];
}): Promise<Map<string, AssetUsageOwner[]>> => {
    const fileIds = uniqueStrings(args.fileIds);
    const usageByFileId = new Map<string, AssetUsageOwner[]>();
    if (fileIds.length === 0) return usageByFileId;

    const owners = createOwners(args);
    for (const owner of owners) {
        await scanOwner({
            owner,
            storage: getStorageShape(owner.kind),
            fileIds,
            usageByFileId,
        });
    }

    return usageByFileId;
};

const scanProjectOwnersForUsage = async (args: {
    fileIds: string[];
    compositions?: ProjectRecordLike[];
    sequences?: ProjectRecordLike[];
}): Promise<Map<string, AssetUsageOwner[]>> => {
    try {
        const fastUsage = await scanProjectOwnersForUsageViaClipQuery(args);
        if (fastUsage.size > 0) {
            return fastUsage;
        }
    } catch (error) {
        console.warn("[AssetReferenceUsage] Fast usage lookup failed; falling back to owner traversal.", error);
    }

    return scanProjectOwnersForUsageByOwnerTraversal(args);
};

export const findProjectFileUsage = async (args: {
    fileId: string;
    compositions?: ProjectRecordLike[];
    sequences?: ProjectRecordLike[];
}): Promise<FileUsageResult> => {
    const fileId = normalizeId(args.fileId);
    if (!fileId) return { fileId: "", owners: [] };

    const usageByFileId = await scanProjectOwnersForUsage({
        fileIds: [fileId],
        compositions: args.compositions,
        sequences: args.sequences,
    });

    return {
        fileId,
        owners: usageByFileId.get(fileId) ?? [],
    };
};

export const findFirstBlockedProjectFile = async (args: {
    fileIds: string[];
    compositions?: ProjectRecordLike[];
    sequences?: ProjectRecordLike[];
}): Promise<FileUsageResult | null> => {
    const fileIds = uniqueStrings(args.fileIds);
    if (fileIds.length === 0) return null;

    const usageByFileId = await scanProjectOwnersForUsage({
        fileIds,
        compositions: args.compositions,
        sequences: args.sequences,
    });

    for (const fileId of fileIds) {
        const owners = usageByFileId.get(fileId) ?? [];
        if (owners.length > 0) {
            return { fileId, owners };
        }
    }

    return null;
};
